import type { Viewport, BoundingBox } from "../state/types";
import { applyTransform } from "./viewport";
import { CONFIDENCE_HIGH, CONFIDENCE_MEDIUM } from "@capture/shared";

interface RenderParams {
  ctx: CanvasRenderingContext2D;
  viewport: Viewport;
  pageBitmap: ImageBitmap | null;
  pageWidth: number;
  pageHeight: number;
  /** Backing-store dimensions, in DEVICE pixels (canvas.width/height). */
  canvasWidth: number;
  canvasHeight: number;
  /**
   * F-110 — the display's device-pixel ratio. The clear below runs at identity
   * and so uses the device-pixel dimensions above; everything after
   * `applyTransform` is expressed in CSS pixels on top of the dpr base.
   * Defaults to 1 so non-Retina rendering is unchanged.
   */
  devicePixelRatio?: number;
  fieldBoxes: { box: BoundingBox; isActive: boolean; confidence: number | null }[];
  /**
   * R-177 — admin-drawn extraction zones, rendered as an always-visible
   * layer beneath the OCR confidence boxes. Slate stroke for inactive
   * fields, copper stroke + tinted fill for the active field — mirrors the
   * Zones-tab convention. Defaults to empty when callers don't pass it
   * (test-helper compatibility + the layer can be safely absent on canvases
   * that don't have a job-type / field-definition context).
   */
  zoneBoxes?: { zone: BoundingBox; isActive: boolean }[];
  /** When false, inactive field confidence outlines are hidden. Active field focus outline is unaffected. */
  showBboxOverlays?: boolean;
}

export function renderFrame(params: RenderParams): void {
  const {
    ctx,
    viewport,
    pageBitmap,
    canvasWidth,
    canvasHeight,
    fieldBoxes,
    zoneBoxes = [],
    showBboxOverlays = true,
    devicePixelRatio = 1,
  } = params;

  // Clear
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = "#0e0e0e";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  if (!pageBitmap) return;

  // Apply viewport transform
  applyTransform(ctx, viewport, devicePixelRatio);

  // Draw page image.
  //
  // R-190 — guard against detached/closed ImageBitmap. End-of-batch and
  // doc-to-doc transitions can dispose the current page's bitmap
  // (`PrefetchManager.freeEntry → bitmap.close()`) one frame BEFORE the
  // queued requestAnimationFrame fires. drawImage on a detached bitmap
  // throws a synchronous `DOMException("InvalidStateError")`. Swallow that
  // SPECIFIC exception (the next mount will re-bitmap from scratch) and
  // bail before overlay rendering — drawing field/zone overlays on top of
  // a cleared background without the page underneath is misleading. Any
  // other error (SecurityError, programmer bugs, etc.) must surface.
  try {
    ctx.drawImage(pageBitmap, 0, 0);
  } catch (err) {
    if (err instanceof DOMException && err.name === "InvalidStateError") {
      return;
    }
    throw err;
  }

  // R-177 — drawn-zone layer BELOW the OCR confidence boxes. Always
  // visible regardless of `showBboxOverlays` (that toggle is for the
  // OCR-confidence layer specifically). Active field gets a brighter
  // copper stroke + tint; inactive fields get a muted slate stroke so
  // the page text underneath stays readable.
  for (const { zone, isActive } of zoneBoxes) {
    const zx = zone.x * pageBitmap.width;
    const zy = zone.y * pageBitmap.height;
    const zw = zone.width * pageBitmap.width;
    const zh = zone.height * pageBitmap.height;

    if (isActive) {
      ctx.strokeStyle = "#c27040"; // copper — Skarn accent, same as Zones-tab active
      ctx.lineWidth = 1.5 / viewport.scale;
      ctx.strokeRect(zx, zy, zw, zh);
      ctx.fillStyle = "rgba(194, 112, 64, 0.10)";
      ctx.fillRect(zx, zy, zw, zh);
    } else {
      ctx.strokeStyle = "rgba(100, 116, 139, 0.55)"; // slate-500 — Zones-tab guide convention
      ctx.lineWidth = 1 / viewport.scale;
      ctx.setLineDash([3 / viewport.scale, 3 / viewport.scale]);
      ctx.strokeRect(zx, zy, zw, zh);
      ctx.setLineDash([]);
    }
  }

  // Draw field bounding boxes
  for (const { box, isActive, confidence } of fieldBoxes) {
    const x = box.x * pageBitmap.width;
    const y = box.y * pageBitmap.height;
    const w = box.width * pageBitmap.width;
    const h = box.height * pageBitmap.height;

    if (isActive) {
      // Active field — prominent highlight
      ctx.strokeStyle = "#ea580c";
      ctx.lineWidth = 2 / viewport.scale;
      ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = "rgba(234, 88, 12, 0.08)";
      ctx.fillRect(x, y, w, h);
    } else if (showBboxOverlays) {
      // Inactive field — subtle confidence-color overlay (hidden when showBboxOverlays=false)
      const color = getConfidenceColor(confidence);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1 / viewport.scale;
      ctx.setLineDash([4 / viewport.scale, 2 / viewport.scale]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }
}

export function getConfidenceColor(confidence: number | null): string {
  if (confidence === null) return "rgba(150, 150, 150, 0.4)";
  if (confidence >= CONFIDENCE_HIGH) return "rgba(34, 197, 94, 0.4)";
  if (confidence >= CONFIDENCE_MEDIUM) return "rgba(234, 179, 8, 0.4)";
  return "rgba(239, 68, 68, 0.5)";
}
